/**
 * NETUNO — Official Launch Presentation
 * Premium HTML Slides — Fixed proportions, platform screenshots, improved typography
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ChevronLeft, ChevronRight, Maximize, Minimize, Home, Grid3X3,
  Shield, AlertTriangle, BarChart3, Brain, Zap, Target, TrendingUp,
  CheckCircle2, ArrowRight, Layers, Search, Bell, FileCheck, Activity,
  GitCompareArrows, Database, Cpu, RefreshCw, Eye, Award, Globe,
  Factory, Droplets, Wind, HardHat, Boxes, LineChart
} from 'lucide-react';

// Images
import heroBg from '@/assets/presentation/hero-bg.jpg';
import problemChaos from '@/assets/presentation/problem-chaos.jpg';
import marketData from '@/assets/presentation/market-data.jpg';
import opportunityImg from '@/assets/presentation/opportunity.jpg';
import rkgGraph from '@/assets/presentation/rkg-graph.jpg';
import nichesImg from '@/assets/presentation/niches.jpg';
import engineImg from '@/assets/presentation/engine.jpg';
import closingImg from '@/assets/presentation/closing.jpg';
import screenDashboard from '@/assets/presentation/screen-dashboard.jpg';
import screenInspections from '@/assets/presentation/screen-inspections.jpg';
import screenMontecarlo from '@/assets/presentation/screen-montecarlo.jpg';
import screenAlerts from '@/assets/presentation/screen-alerts.jpg';

// ─── Constants ───────────────────────────────────────────
const SLIDE_W = 1920;
const SLIDE_H = 1080;

// ─── Styles ──────────────────────────────────────────────
const S = {
  slide: 'absolute w-[1920px] h-[1080px] overflow-hidden select-none',
  darkBg: 'bg-[#0a1628]',
  gradient: 'bg-gradient-to-br from-[#0a1628] via-[#0f2847] to-[#0a1628]',
  badge: 'inline-flex items-center gap-3 px-5 py-2.5 rounded-full bg-[#2dd4bf]/10 text-[#2dd4bf] text-[20px] font-medium border border-[#2dd4bf]/20',
  card: 'bg-white/[0.04] backdrop-blur-sm border border-white/10 rounded-2xl',
  iconBox: 'w-14 h-14 rounded-xl bg-[#2dd4bf]/10 flex items-center justify-center',
} as const;

// ─── Slide Components ────────────────────────────────────

const SlideCover = () => (
  <div className={`${S.slide} relative`}>
    <img src={heroBg} alt="" className="absolute inset-0 w-full h-full object-cover" />
    <div className="absolute inset-0 bg-gradient-to-t from-[#0a1628] via-[#0a1628]/70 to-transparent" />
    <div className="relative z-10 flex flex-col items-center justify-center h-full text-center px-48">
      <div className={`${S.badge} text-[22px] mb-12`}>
        <Zap className="w-6 h-6" /> Risk Intelligence Platform
      </div>
      <h1 className="text-[130px] font-black tracking-[-0.04em] text-white mb-10 leading-none">
        NETUNO
      </h1>
      <p className="text-[36px] text-[#7dd3fc] font-light max-w-[1200px] leading-[1.4]">
        Inteligência operacional para gestão de contratos,<br />riscos e desempenho em obras e projetos industriais
      </p>
      <div className="mt-24 flex items-center gap-4 text-[20px] text-white/40">
        <div className="w-16 h-[1px] bg-white/20" />
        Apresentação Oficial de Lançamento
        <div className="w-16 h-[1px] bg-white/20" />
      </div>
    </div>
  </div>
);

const SlideProblem = () => (
  <div className={`${S.slide} ${S.gradient} flex`}>
    <div className="w-[55%] flex flex-col justify-center pl-28 pr-16">
      <span className={S.badge}><AlertTriangle className="w-6 h-6" /> O Problema</span>
      <h2 className="text-[60px] font-bold text-white mt-10 mb-12 leading-[1.1] tracking-tight">
        O mercado opera<br />no escuro
      </h2>
      <div className="space-y-7">
        {[
          { icon: Eye, text: 'Falta de visibilidade sobre riscos em tempo real' },
          { icon: AlertTriangle, text: 'Atrasos crônicos — 80% dos projetos excedem o prazo' },
          { icon: BarChart3, text: 'Risco mal quantificado — decisões baseadas em intuição' },
          { icon: Layers, text: 'Dados fragmentados em planilhas desconectadas' },
          { icon: Database, text: 'Zero aprendizado entre projetos' },
        ].map(({ icon: Icon, text }, i) => (
          <div key={i} className="flex items-center gap-6 text-[26px] text-[#e2e8f0]">
            <div className="w-12 h-12 rounded-xl bg-red-500/10 flex items-center justify-center shrink-0">
              <Icon className="w-6 h-6 text-red-400" />
            </div>
            {text}
          </div>
        ))}
      </div>
    </div>
    <div className="w-[45%] relative">
      <img src={problemChaos} alt="" className="absolute inset-0 w-full h-full object-cover" />
      <div className="absolute inset-0 bg-gradient-to-r from-[#0a1628] via-transparent to-transparent" />
    </div>
  </div>
);

const SlideMarketSize = () => (
  <div className={`${S.slide} ${S.gradient} flex`}>
    <div className="w-[48%] relative">
      <img src={marketData} alt="" className="absolute inset-0 w-full h-full object-cover opacity-80" />
      <div className="absolute inset-0 bg-gradient-to-l from-[#0a1628] to-transparent" />
    </div>
    <div className="w-[52%] flex flex-col justify-center pl-20 pr-28">
      <span className={S.badge}><BarChart3 className="w-6 h-6" /> Tamanho do Problema</span>
      <h2 className="text-[56px] font-bold text-white mt-10 mb-14 leading-[1.1] tracking-tight">
        Perdas bilionárias<br />e evitáveis
      </h2>
      <div className="grid grid-cols-2 gap-8">
        {[
          { value: '80%', label: 'dos projetos excedem o prazo original', color: 'text-red-400' },
          { value: '28%', label: 'é o estouro médio de custo', color: 'text-orange-400' },
          { value: '< 15%', label: 'do setor usa analytics preditivo', color: 'text-amber-400' },
          { value: 'US$ 1.6T', label: 'perdas anuais por ineficiência', color: 'text-red-400' },
        ].map(({ value, label, color }, i) => (
          <div key={i} className={`${S.card} p-8`}>
            <p className={`text-[48px] font-bold ${color} mb-3`}>{value}</p>
            <p className="text-[20px] text-[#94a3b8] leading-snug">{label}</p>
          </div>
        ))}
      </div>
    </div>
  </div>
);

const SlideOpportunity = () => (
  <div className={`${S.slide} relative`}>
    <img src={opportunityImg} alt="" className="absolute inset-0 w-full h-full object-cover" />
    <div className="absolute inset-0 bg-[#0a1628]/80" />
    <div className="relative z-10 flex flex-col justify-center h-full px-28">
      <span className={S.badge}><TrendingUp className="w-6 h-6" /> A Oportunidade</span>
      <h2 className="text-[60px] font-bold text-white mt-10 mb-8 leading-[1.1] tracking-tight">O momento é agora</h2>
      <p className="text-[28px] text-[#94a3b8] max-w-[900px] mb-16 leading-relaxed">
        A convergência de IoT, cloud e IA cria a janela perfeita para plataformas de inteligência operacional no setor de infraestrutura.
      </p>
      <div className="grid grid-cols-3 gap-10 max-w-[1500px]">
        {[
          { icon: Globe, title: 'Digitalização Acelerada', desc: 'Pressão ESG e regulatória forçam rastreabilidade total' },
          { icon: Brain, title: 'IA Madura', desc: 'Modelos preditivos acessíveis e escaláveis em cloud' },
          { icon: Target, title: 'Mercado Fragmentado', desc: 'Nenhum player integra execução, risco e analytics' },
        ].map(({ icon: Icon, title, desc }, i) => (
          <div key={i} className={`${S.card} p-10`}>
            <div className={S.iconBox}><Icon className="w-7 h-7 text-[#2dd4bf]" /></div>
            <h3 className="text-[28px] font-semibold text-white mt-7 mb-4">{title}</h3>
            <p className="text-[22px] text-[#94a3b8] leading-relaxed">{desc}</p>
          </div>
        ))}
      </div>
    </div>
  </div>
);

const SlideIntro = () => (
  <div className={`${S.slide} ${S.gradient} flex flex-col items-center justify-center text-center px-48`}>
    <span className={S.badge}><Zap className="w-6 h-6" /> Apresentando</span>
    <h2 className="text-[100px] font-black text-white mt-10 mb-8 tracking-[-0.03em]">NETUNO</h2>
    <p className="text-[34px] text-[#7dd3fc] font-light mb-20 max-w-[1000px]">
      Risk Intelligence + Operational Analytics<br />em uma única plataforma
    </p>
    <div className="grid grid-cols-4 gap-8 max-w-[1600px]">
      {[
        { icon: Search, label: 'Detectar', desc: 'Captura automatizada de desvios em campo' },
        { icon: Brain, label: 'Explicar', desc: 'Diagnóstico causal e classificação de risco' },
        { icon: Activity, label: 'Prever', desc: 'Simulação Monte Carlo e projeções preditivas' },
        { icon: Target, label: 'Sugerir', desc: 'Ações corretivas priorizadas por impacto' },
      ].map(({ icon: Icon, label, desc }, i) => (
        <div key={i} className={`${S.card} p-10 text-left relative`}>
          <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-[#2dd4bf]/20 to-[#7dd3fc]/20 flex items-center justify-center mb-6">
            <Icon className="w-7 h-7 text-[#2dd4bf]" />
          </div>
          <h3 className="text-[30px] font-bold text-white mb-3">{label}</h3>
          <p className="text-[20px] text-[#94a3b8] leading-relaxed">{desc}</p>
        </div>
      ))}
    </div>
  </div>
);

const SlideClassification = () => (
  <div className={`${S.slide} ${S.gradient} flex flex-col justify-center px-28`}>
    <span className={S.badge}><Award className="w-6 h-6" /> Classificação</span>
    <h2 className="text-[56px] font-bold text-white mt-10 mb-16 leading-[1.1] tracking-tight">
      Uma nova categoria<br />de plataforma
    </h2>
    <div className="flex gap-10">
      <div className="flex-1">
        <div className={`${S.card} p-12 border-[#2dd4bf]/30 bg-[#2dd4bf]/5`}>
          <div className="flex items-center gap-6 mb-8">
            <div className="w-16 h-16 rounded-2xl bg-[#2dd4bf]/20 flex items-center justify-center shrink-0">
              <Shield className="w-8 h-8 text-[#2dd4bf]" />
            </div>
            <div>
              <p className="text-[16px] text-[#2dd4bf] uppercase tracking-[0.15em] font-semibold mb-1">Categoria Principal</p>
              <h3 className="text-[34px] font-bold text-white leading-tight">Construction Contract<br />Intelligence Platform</h3>
            </div>
          </div>
          <p className="text-[22px] text-[#94a3b8] leading-relaxed">
            Integra CLM + Construction Management + Decision Intelligence em um pipeline analítico unificado.
          </p>
        </div>
      </div>
      <div className="flex-1 space-y-5">
        {[
          'Risk Intelligence Platform',
          'Operational Analytics System',
          'Contract Intelligence Engine',
          'Project Risk Management Platform',
        ].map((cat, i) => (
          <div key={i} className={`${S.card} p-7 flex items-center gap-5`}>
            <CheckCircle2 className="w-7 h-7 text-[#2dd4bf]/60 shrink-0" />
            <span className="text-[24px] text-[#e2e8f0]">{cat}</span>
          </div>
        ))}
      </div>
    </div>
  </div>
);

const SlideNiches = () => (
  <div className={`${S.slide} relative`}>
    <img src={nichesImg} alt="" className="absolute inset-0 w-full h-full object-cover" />
    <div className="absolute inset-0 bg-[#0a1628]/85" />
    <div className="relative z-10 flex flex-col justify-center h-full px-28">
      <span className={S.badge}><Globe className="w-6 h-6" /> Mercados-Alvo</span>
      <h2 className="text-[56px] font-bold text-white mt-10 mb-16 leading-[1.1] tracking-tight">Nichos de alto impacto</h2>
      <div className="grid grid-cols-3 gap-8 max-w-[1500px]">
        {[
          { icon: HardHat, name: 'Construção Pesada', desc: 'Pontes, rodovias, ferrovias' },
          { icon: Droplets, name: 'Óleo & Gás', desc: 'Plataformas, refinarias, dutos' },
          { icon: Wind, name: 'Energia', desc: 'Eólica, solar, hidrelétrica' },
          { icon: Factory, name: 'Mineração', desc: 'Plantas, barragens, beneficiamento' },
          { icon: Boxes, name: 'EPC Contractors', desc: 'Grandes empreiteiras integradas' },
          { icon: Layers, name: 'Infraestrutura Pública', desc: 'Saneamento, mobilidade, portos' },
        ].map(({ icon: Icon, name, desc }, i) => (
          <div key={i} className={`${S.card} p-9`}>
            <Icon className="w-10 h-10 text-[#2dd4bf] mb-5" />
            <h3 className="text-[26px] font-semibold text-white mb-3">{name}</h3>
            <p className="text-[20px] text-[#94a3b8]">{desc}</p>
          </div>
        ))}
      </div>
    </div>
  </div>
);

const SlidePlatformVision = () => (
  <div className={`${S.slide} ${S.gradient} flex flex-col justify-center px-28`}>
    <span className={S.badge}><Layers className="w-6 h-6" /> Visão da Plataforma</span>
    <h2 className="text-[56px] font-bold text-white mt-10 mb-16 leading-[1.1] tracking-tight">Módulos integrados</h2>
    <div className="grid grid-cols-4 gap-6 max-w-[1600px]">
      {[
        { icon: Search, name: 'Inspeções', desc: 'Captura digital com geolocalização e evidências' },
        { icon: Shield, name: 'Gestão de Risco', desc: 'Classificação automática e scoring contínuo' },
        { icon: Bell, name: 'Alertas', desc: 'Detecção proativa de anomalias e desvios' },
        { icon: FileCheck, name: 'Ações Corretivas', desc: 'CAPA com rastreabilidade ponta a ponta' },
        { icon: BarChart3, name: 'Analytics', desc: 'Dashboards executivos e drill-down por contrato' },
        { icon: Activity, name: 'Simulação MC', desc: 'Monte Carlo com distribuições calibradas' },
        { icon: Brain, name: 'Risk Knowledge Graph', desc: 'Inferência causal e aprendizado incremental' },
        { icon: LineChart, name: 'Portfolio', desc: 'Visão consolidada multi-contrato' },
      ].map(({ icon: Icon, name, desc }, i) => (
        <div key={i} className={`${S.card} p-7`}>
          <Icon className="w-9 h-9 text-[#2dd4bf] mb-4" />
          <h3 className="text-[22px] font-semibold text-white mb-2">{name}</h3>
          <p className="text-[18px] text-[#94a3b8] leading-snug">{desc}</p>
        </div>
      ))}
    </div>
  </div>
);

const SlideArchitecture = () => (
  <div className={`${S.slide} ${S.gradient} flex flex-col justify-center items-center px-20`}>
    <span className={S.badge}><Cpu className="w-6 h-6" /> Arquitetura Conceitual</span>
    <h2 className="text-[56px] font-bold text-white mt-10 mb-20 text-center tracking-tight">Pipeline Analítico</h2>
    <div className="flex items-center gap-3 max-w-[1700px]">
      {[
        { icon: Search, label: 'Inspeções\nde Campo', color: 'from-blue-500/20 to-blue-600/20' },
        { icon: Shield, label: 'Classificação\nde Risco', color: 'from-cyan-500/20 to-cyan-600/20' },
        { icon: Bell, label: 'Alertas\nInteligentes', color: 'from-teal-500/20 to-teal-600/20' },
        { icon: FileCheck, label: 'Ações\nCorretivas', color: 'from-emerald-500/20 to-emerald-600/20' },
        { icon: BarChart3, label: 'Analytics\nOperacional', color: 'from-green-500/20 to-green-600/20' },
        { icon: Activity, label: 'Simulação\nMonte Carlo', color: 'from-lime-500/20 to-lime-600/20' },
        { icon: Brain, label: 'Inteligência\nde Risco', color: 'from-yellow-500/20 to-yellow-600/20' },
      ].map(({ icon: Icon, label, color }, i) => (
        <React.Fragment key={i}>
          <div className={`flex flex-col items-center gap-4 bg-gradient-to-b ${color} border border-white/10 rounded-2xl p-6 min-w-[180px]`}>
            <div className="w-14 h-14 rounded-xl bg-white/10 flex items-center justify-center">
              <Icon className="w-7 h-7 text-[#2dd4bf]" />
            </div>
            <p className="text-[18px] font-medium text-white text-center whitespace-pre-line leading-snug">{label}</p>
          </div>
          {i < 6 && <ArrowRight className="w-6 h-6 text-[#2dd4bf]/40 shrink-0" />}
        </React.Fragment>
      ))}
    </div>
    <p className="text-[22px] text-[#94a3b8] mt-16 text-center max-w-[1000px] leading-relaxed">
      Cada etapa alimenta a próxima, criando um ciclo de aprendizado contínuo que melhora as previsões a cada iteração.
    </p>
  </div>
);

const SlideDemoSystem = () => (
  <div className={`${S.slide} ${S.gradient} flex flex-col justify-center px-28`}>
    <span className={S.badge}><Eye className="w-6 h-6" /> O Sistema</span>
    <h2 className="text-[56px] font-bold text-white mt-10 mb-14 leading-[1.1] tracking-tight">Interface projetada para decisão</h2>
    <div className="grid grid-cols-2 gap-8 max-w-[1600px]">
      {[
        { title: 'Dashboard Executivo', desc: 'KPIs de risco, alertas e exposição financeira em tempo real', img: screenDashboard },
        { title: 'Inspeções de Campo', desc: 'Captura geolocalizada com evidências e classificação automática', img: screenInspections },
        { title: 'Simulação Monte Carlo', desc: 'P50/P80/P90, tendências e probabilidades de desvio', img: screenMontecarlo },
        { title: 'Gestão de Alertas', desc: 'Fila priorizada com scoring e ações corretivas vinculadas', img: screenAlerts },
      ].map(({ title, desc, img }, i) => (
        <div key={i} className="bg-[#0f2847] border border-white/10 rounded-2xl overflow-hidden">
          <div className="h-[210px] overflow-hidden">
            <img src={img} alt={title} className="w-full h-full object-cover object-top" />
          </div>
          <div className="px-8 py-6">
            <h3 className="text-[24px] font-semibold text-white mb-2">{title}</h3>
            <p className="text-[19px] text-[#94a3b8]">{desc}</p>
          </div>
        </div>
      ))}
    </div>
  </div>
);

const SlideFeatures = () => (
  <div className={`${S.slide} ${S.gradient} flex flex-col justify-center px-28`}>
    <span className={S.badge}><Zap className="w-6 h-6" /> Funcionalidades</span>
    <h2 className="text-[56px] font-bold text-white mt-10 mb-16 leading-[1.1] tracking-tight">Tudo que você precisa</h2>
    <div className="grid grid-cols-3 gap-x-20 gap-y-8 max-w-[1500px]">
      {[
        'Inspeções digitais georreferenciadas',
        'Classificação automática de risco (NLP)',
        'Alertas inteligentes com scoring',
        'CAPA — Ações corretivas rastreáveis',
        'Analytics operacional multi-dimensão',
        'Simulação Monte Carlo calibrada',
        'Análise de recorrência e padrões',
        'Diagnóstico de causas sistêmicas',
        'Dashboards executivos em tempo real',
        'Gestão de contratos e aditivos',
        'Medições e boletins de campo',
        'Portfolio multi-contrato consolidado',
      ].map((feat, i) => (
        <div key={i} className="flex items-center gap-5 text-[24px]">
          <CheckCircle2 className="w-7 h-7 text-[#2dd4bf] shrink-0" />
          <span className="text-[#e2e8f0]">{feat}</span>
        </div>
      ))}
    </div>
  </div>
);

const SlidePainSolution = () => (
  <div className={`${S.slide} ${S.gradient} flex flex-col justify-center px-28`}>
    <span className={S.badge}><Target className="w-6 h-6" /> Dores → Soluções</span>
    <h2 className="text-[56px] font-bold text-white mt-10 mb-14 leading-[1.1] tracking-tight">Cada dor tem uma resposta</h2>
    <div className="space-y-6 max-w-[1600px]">
      {[
        { pain: 'Decisões baseadas em intuição', solution: 'Scoring de risco quantificado com dados reais' },
        { pain: 'Atrasos descobertos tarde demais', solution: 'Alertas proativos com detecção antecipada' },
        { pain: 'Planilhas desconectadas', solution: 'Plataforma unificada com dados em tempo real' },
        { pain: 'Sem aprendizado entre projetos', solution: 'Risk Knowledge Graph com memória incremental' },
        { pain: 'Custo de retrabalho imprevisível', solution: 'Simulação MC com P50/P80/P90 calibrados' },
      ].map(({ pain, solution }, i) => (
        <div key={i} className="flex items-center gap-6">
          <div className="flex-1 bg-red-500/10 border border-red-500/20 rounded-xl px-8 py-5 flex items-center gap-5">
            <AlertTriangle className="w-6 h-6 text-red-400 shrink-0" />
            <span className="text-[22px] text-red-300">{pain}</span>
          </div>
          <ArrowRight className="w-8 h-8 text-[#2dd4bf] shrink-0" />
          <div className="flex-1 bg-[#2dd4bf]/10 border border-[#2dd4bf]/20 rounded-xl px-8 py-5 flex items-center gap-5">
            <CheckCircle2 className="w-6 h-6 text-[#2dd4bf] shrink-0" />
            <span className="text-[22px] text-[#2dd4bf]">{solution}</span>
          </div>
        </div>
      ))}
    </div>
  </div>
);

const SlideTechDifferentials = () => (
  <div className={`${S.slide} ${S.gradient} flex flex-col justify-center px-28`}>
    <span className={S.badge}><Cpu className="w-6 h-6" /> Diferenciais</span>
    <h2 className="text-[56px] font-bold text-white mt-10 mb-16 leading-[1.1] tracking-tight">Tecnologia de próxima geração</h2>
    <div className="grid grid-cols-2 gap-10 max-w-[1600px]">
      {[
        { icon: Brain, title: 'Risk Knowledge Graph', desc: 'Grafo de conhecimento que mapeia relações causais entre desvios, fatores de risco e impactos em prazo/custo.' },
        { icon: Activity, title: 'Monte Carlo Calibrado', desc: 'Simulações com distribuições ajustadas por dados reais de campo. P50, P80, P90 com intervalos de confiança.' },
        { icon: RefreshCw, title: 'Aprendizado Incremental', desc: 'Motor preditivo que melhora automaticamente a cada resultado observado. Calibração contínua.' },
        { icon: GitCompareArrows, title: 'Shadow Mode & Fallback', desc: 'Novo motor opera em paralelo ao legado. Ativação gradual com fallback automático.' },
      ].map(({ icon: Icon, title, desc }, i) => (
        <div key={i} className={`${S.card} p-10`}>
          <div className={S.iconBox}><Icon className="w-7 h-7 text-[#2dd4bf]" /></div>
          <h3 className="text-[30px] font-semibold text-white mt-7 mb-4">{title}</h3>
          <p className="text-[22px] text-[#94a3b8] leading-relaxed">{desc}</p>
        </div>
      ))}
    </div>
  </div>
);

const SlideRKG = () => (
  <div className={`${S.slide} relative`}>
    <img src={rkgGraph} alt="" className="absolute inset-0 w-full h-full object-cover" />
    <div className="absolute inset-0 bg-[#0a1628]/60" />
    <div className="relative z-10 flex flex-col justify-center h-full px-28">
      <span className={S.badge}><Brain className="w-6 h-6" /> Risk Knowledge Graph</span>
      <h2 className="text-[64px] font-bold text-white mt-10 mb-10 leading-[1.1] tracking-tight">
        O cérebro do<br />Netuno
      </h2>
      <p className="text-[28px] text-[#e2e8f0] max-w-[750px] mb-16 leading-relaxed">
        Um grafo de conhecimento que conecta desvios de campo a causas raiz e projeta impactos em prazo e custo com precisão crescente.
      </p>
      <div className="flex gap-8 max-w-[1100px]">
        {[
          { label: 'Fatores de Risco', value: '120+', sub: 'variáveis monitoradas' },
          { label: 'Relações Causais', value: '500+', sub: 'conexões mapeadas' },
          { label: 'Precisão', value: '<15%', sub: 'MAPE em backtesting' },
        ].map(({ label, value, sub }, i) => (
          <div key={i} className="bg-black/40 backdrop-blur border border-white/10 rounded-xl p-8 flex-1">
            <p className="text-[16px] text-[#2dd4bf] uppercase tracking-[0.12em] font-semibold mb-3">{label}</p>
            <p className="text-[52px] font-bold text-white leading-none mb-2">{value}</p>
            <p className="text-[18px] text-[#94a3b8]">{sub}</p>
          </div>
        ))}
      </div>
    </div>
  </div>
);

const SlideEngine = () => (
  <div className={`${S.slide} relative`}>
    <img src={engineImg} alt="" className="absolute inset-0 w-full h-full object-cover opacity-60" />
    <div className="absolute inset-0 bg-[#0a1628]/70" />
    <div className="relative z-10 flex flex-col justify-center h-full px-28">
      <span className={S.badge}><Activity className="w-6 h-6" /> Motor Analítico</span>
      <h2 className="text-[56px] font-bold text-white mt-10 mb-16 leading-[1.1] tracking-tight">Quatro camadas de inteligência</h2>
      <div className="grid grid-cols-4 gap-8 max-w-[1600px]">
        {[
          { num: '01', title: 'Analytics Operacional', desc: 'Métricas de campo, cobertura, performance, tendências' },
          { num: '02', title: 'Inferência de Risco', desc: 'Classificação NLP, scoring multidimensional, causas' },
          { num: '03', title: 'Simulação MC', desc: '10.000 iterações, distribuições calibradas, P50/P80/P90' },
          { num: '04', title: 'Aprendizado', desc: 'Feedback loop, calibração incremental, validação Brier' },
        ].map(({ num, title, desc }, i) => (
          <div key={i} className="bg-black/40 backdrop-blur border border-white/10 rounded-2xl p-8">
            <span className="text-[52px] font-black text-[#2dd4bf]/20">{num}</span>
            <h3 className="text-[24px] font-semibold text-white mt-3 mb-4">{title}</h3>
            <p className="text-[19px] text-[#94a3b8] leading-snug">{desc}</p>
          </div>
        ))}
      </div>
    </div>
  </div>
);

const SlideBenefits = () => (
  <div className={`${S.slide} ${S.gradient} flex flex-col justify-center px-28`}>
    <span className={S.badge}><TrendingUp className="w-6 h-6" /> Benefícios</span>
    <h2 className="text-[56px] font-bold text-white mt-10 mb-16 leading-[1.1] tracking-tight">Resultados mensuráveis</h2>
    <div className="grid grid-cols-4 gap-8 max-w-[1600px]">
      {[
        { value: '40%', label: 'Redução de atrasos', desc: 'Detecção antecipada e gestão proativa de riscos', icon: TrendingUp },
        { value: '3x', label: 'Previsibilidade', desc: 'Projeções com intervalos de confiança calibrados', icon: Target },
        { value: '60%', label: 'Menos retrabalho', desc: 'Ações corretivas direcionadas por impacto', icon: RefreshCw },
        { value: '∞', label: 'Aprendizado contínuo', desc: 'Motor que melhora a cada projeto concluído', icon: Brain },
      ].map(({ value, label, desc, icon: Icon }, i) => (
        <div key={i} className={`${S.card} p-10 text-center`}>
          <div className="w-14 h-14 rounded-2xl bg-[#2dd4bf]/10 flex items-center justify-center mx-auto mb-6">
            <Icon className="w-7 h-7 text-[#2dd4bf]" />
          </div>
          <p className="text-[56px] font-black text-white mb-3">{value}</p>
          <p className="text-[24px] font-semibold text-[#2dd4bf] mb-4">{label}</p>
          <p className="text-[19px] text-[#94a3b8] leading-snug">{desc}</p>
        </div>
      ))}
    </div>
  </div>
);

const SlideComparison = () => (
  <div className={`${S.slide} ${S.gradient} flex flex-col justify-center px-28`}>
    <span className={S.badge}><GitCompareArrows className="w-6 h-6" /> Posicionamento</span>
    <h2 className="text-[56px] font-bold text-white mt-10 mb-16 leading-[1.1] tracking-tight">Netuno vs. mercado</h2>
    <div className="max-w-[1500px]">
      <table className="w-full">
        <thead>
          <tr className="border-b border-white/10">
            <th className="text-left text-[22px] text-[#94a3b8] py-5 pr-8 font-medium w-[320px]">Capacidade</th>
            <th className="text-center text-[22px] text-[#94a3b8] py-5 font-medium">Procore</th>
            <th className="text-center text-[22px] text-[#94a3b8] py-5 font-medium">Autodesk</th>
            <th className="text-center text-[22px] text-[#94a3b8] py-5 font-medium">Oracle</th>
            <th className="text-center text-[22px] py-5 font-bold text-[#2dd4bf]">NETUNO</th>
          </tr>
        </thead>
        <tbody>
          {[
            { feature: 'Gestão de Contratos', procore: true, autodesk: false, oracle: true, netuno: true },
            { feature: 'Inspeções de Campo', procore: true, autodesk: true, oracle: false, netuno: true },
            { feature: 'Analytics de Risco', procore: false, autodesk: false, oracle: true, netuno: true },
            { feature: 'Simulação Monte Carlo', procore: false, autodesk: false, oracle: false, netuno: true },
            { feature: 'Risk Knowledge Graph', procore: false, autodesk: false, oracle: false, netuno: true },
            { feature: 'Aprendizado Incremental', procore: false, autodesk: false, oracle: false, netuno: true },
            { feature: 'Classificação NLP', procore: false, autodesk: false, oracle: false, netuno: true },
            { feature: 'Alertas Inteligentes', procore: true, autodesk: false, oracle: true, netuno: true },
          ].map(({ feature, procore, autodesk, oracle, netuno }, i) => (
            <tr key={i} className="border-b border-white/5">
              <td className="py-4 pr-8 text-[22px] text-[#e2e8f0]">{feature}</td>
              {[procore, autodesk, oracle].map((v, j) => (
                <td key={j} className="py-4 text-center">
                  {v ? <CheckCircle2 className="w-6 h-6 text-white/30 mx-auto" /> : <span className="text-white/10 text-[22px]">—</span>}
                </td>
              ))}
              <td className="py-4 text-center">
                {netuno ? <CheckCircle2 className="w-7 h-7 text-[#2dd4bf] mx-auto" /> : <span className="text-white/10">—</span>}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </div>
);

const SlideRoadmap = () => (
  <div className={`${S.slide} ${S.gradient} flex flex-col justify-center px-28`}>
    <span className={S.badge}><LineChart className="w-6 h-6" /> Roadmap</span>
    <h2 className="text-[56px] font-bold text-white mt-10 mb-20 leading-[1.1] tracking-tight">Evolução contínua</h2>
    <div className="relative max-w-[1600px] mx-auto w-full">
      <div className="absolute top-[30px] left-0 right-0 h-[3px] bg-gradient-to-r from-[#2dd4bf]/20 via-[#2dd4bf] to-[#7dd3fc]/20" />
      <div className="flex justify-between relative">
        {[
          { quarter: 'Q1 2026', label: 'Lançamento', items: ['Core Platform', 'Analytics Operacional', 'Monte Carlo v1'], active: true },
          { quarter: 'Q2 2026', label: 'Expansão', items: ['Risk Knowledge Graph', 'Aprendizado Incremental', 'Shadow Mode'], active: true },
          { quarter: 'Q3 2026', label: 'Inteligência', items: ['Predição Autônoma', 'NLP Avançado', 'API Pública'], active: false },
          { quarter: 'Q4 2026', label: 'Escala', items: ['Multi-tenant Enterprise', 'Marketplace de Modelos', 'Mobile Offline'], active: false },
        ].map(({ quarter, label, items, active }, i) => (
          <div key={i} className="flex flex-col items-center w-[320px]">
            <div className={`w-14 h-14 rounded-full ${active ? 'bg-[#2dd4bf]' : 'bg-white/10'} flex items-center justify-center mb-6 border-4 border-[#0a1628] relative z-10`}>
              {active ? <CheckCircle2 className="w-6 h-6 text-[#0a1628]" /> : <div className="w-4 h-4 rounded-full bg-white/30" />}
            </div>
            <p className={`text-[18px] uppercase tracking-[0.12em] font-semibold mb-2 ${active ? 'text-[#2dd4bf]' : 'text-white/40'}`}>{quarter}</p>
            <p className="text-[26px] font-bold text-white mb-5">{label}</p>
            <div className="space-y-3">
              {items.map((item, j) => (
                <p key={j} className={`text-[18px] ${active ? 'text-[#94a3b8]' : 'text-white/30'}`}>{item}</p>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  </div>
);

const SlideCTA = () => (
  <div className={`${S.slide} relative`}>
    <img src={closingImg} alt="" className="absolute inset-0 w-full h-full object-cover" />
    <div className="absolute inset-0 bg-gradient-to-t from-[#0a1628] via-[#0a1628]/80 to-[#0a1628]/40" />
    <div className="relative z-10 flex flex-col items-center justify-center h-full text-center px-48">
      <h2 className="text-[72px] font-black text-white mb-10 leading-[1.1]">
        Transformando dados<br />operacionais em<br /><span className="text-[#2dd4bf]">inteligência de risco</span>
      </h2>
      <p className="text-[30px] text-[#94a3b8] max-w-[900px] mb-14 leading-relaxed">
        O futuro da gestão de contratos e obras começa com decisões informadas por dados — não por intuição.
      </p>
      <div className="flex items-center gap-8">
        <div className="px-12 py-6 bg-[#2dd4bf] text-[#0a1628] rounded-xl text-[26px] font-bold">
          Agende uma Demo
        </div>
        <div className="px-12 py-6 border border-white/20 text-white rounded-xl text-[26px] font-medium">
          Saiba Mais
        </div>
      </div>
    </div>
  </div>
);

const SlideFinal = () => (
  <div className={`${S.slide} relative`}>
    <img src={heroBg} alt="" className="absolute inset-0 w-full h-full object-cover" />
    <div className="absolute inset-0 bg-gradient-to-t from-[#0a1628] via-[#0a1628]/80 to-transparent" />
    <div className="relative z-10 flex flex-col items-center justify-center h-full text-center">
      <h1 className="text-[130px] font-black tracking-[-0.04em] text-white mb-8 leading-none">
        NETUNO
      </h1>
      <div className="w-28 h-[2px] bg-[#2dd4bf] mb-8" />
      <p className="text-[34px] text-[#7dd3fc] font-light">Risk Intelligence Platform</p>
      <div className="mt-24 text-[20px] text-white/30">
        © 2026 Netuno. Todos os direitos reservados.
      </div>
    </div>
  </div>
);

// ─── All slides ──────────────────────────────────────────
const SLIDES = [
  SlideCover, SlideProblem, SlideMarketSize, SlideOpportunity, SlideIntro,
  SlideClassification, SlideNiches, SlidePlatformVision, SlideArchitecture,
  SlideDemoSystem, SlideFeatures, SlidePainSolution, SlideTechDifferentials,
  SlideRKG, SlideEngine, SlideBenefits, SlideComparison, SlideRoadmap,
  SlideCTA, SlideFinal,
];
const TOTAL_SLIDES = SLIDES.length;

// ─── Main Presentation Component ─────────────────────────
const NetunoPresentationPage: React.FC = () => {
  const navigate = useNavigate();
  const [current, setCurrent] = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showGrid, setShowGrid] = useState(false);
  const [scale, setScale] = useState(1);

  const updateScale = useCallback(() => {
    setScale(Math.min(window.innerWidth / SLIDE_W, window.innerHeight / SLIDE_H));
  }, []);

  useEffect(() => {
    updateScale();
    window.addEventListener('resize', updateScale);
    return () => window.removeEventListener('resize', updateScale);
  }, [updateScale]);

  const goTo = (i: number) => { setCurrent(Math.max(0, Math.min(i, TOTAL_SLIDES - 1))); setShowGrid(false); };
  const prev = () => goTo(current - 1);
  const next = () => goTo(current + 1);

  const toggleFullscreen = useCallback(() => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen().then(() => setIsFullscreen(true));
    } else {
      document.exitFullscreen().then(() => setIsFullscreen(false));
    }
  }, []);

  useEffect(() => {
    const h = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', h);
    return () => document.removeEventListener('fullscreenchange', h);
  }, []);

  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'ArrowRight' || e.key === ' ') { e.preventDefault(); next(); }
      else if (e.key === 'ArrowLeft') { e.preventDefault(); prev(); }
      else if (e.key === 'Escape') { if (showGrid) setShowGrid(false); else if (isFullscreen) document.exitFullscreen(); }
      else if (e.key === 'f' || e.key === 'F5') { e.preventDefault(); toggleFullscreen(); }
      else if (e.key === 'g') { setShowGrid(v => !v); }
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [current, showGrid, isFullscreen]);

  const SlideComponent = SLIDES[current];

  if (showGrid) {
    return (
      <div className="fixed inset-0 bg-[#0a1628] z-[9999] overflow-auto p-8">
        <div className="flex items-center justify-between mb-8">
          <h2 className="text-2xl font-bold text-white">Slides ({TOTAL_SLIDES})</h2>
          <button onClick={() => setShowGrid(false)} className="px-4 py-2 bg-white/10 text-white rounded-lg hover:bg-white/20 transition">
            Fechar Grid
          </button>
        </div>
        <div className="grid grid-cols-4 gap-4">
          {SLIDES.map((Slide, i) => (
            <button
              key={i}
              onClick={() => goTo(i)}
              className={`relative rounded-xl overflow-hidden border-2 transition hover:border-[#2dd4bf] ${i === current ? 'border-[#2dd4bf]' : 'border-white/10'}`}
            >
              <div className="relative w-full" style={{ paddingBottom: `${(SLIDE_H / SLIDE_W) * 100}%` }}>
                <div className="absolute inset-0" style={{ transform: `scale(${1 / (SLIDE_W / 400)})`, transformOrigin: 'top left', width: SLIDE_W, height: SLIDE_H }}>
                  <Slide />
                </div>
              </div>
              <div className="absolute bottom-0 left-0 right-0 bg-black/60 px-3 py-2 text-left">
                <span className="text-[12px] text-[#2dd4bf] font-mono">{String(i + 1).padStart(2, '0')}</span>
              </div>
            </button>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-[#0a1628] z-[9999] overflow-hidden cursor-none group">
      <div
        className="absolute"
        style={{
          width: SLIDE_W, height: SLIDE_H,
          left: '50%', top: '50%',
          marginLeft: -SLIDE_W / 2, marginTop: -SLIDE_H / 2,
          transform: `scale(${scale})`,
          transformOrigin: 'center center',
        }}
      >
        <div className="transition-opacity duration-500"><SlideComponent /></div>
      </div>

      {/* Nav bar */}
      <div className="absolute bottom-0 left-0 right-0 h-16 bg-gradient-to-t from-black/60 to-transparent flex items-end justify-center pb-4 gap-4 opacity-0 group-hover:opacity-100 transition-opacity duration-300 cursor-default">
        <button onClick={() => navigate(-1)} className="p-2 text-white/60 hover:text-white transition" title="Voltar"><Home className="w-5 h-5" /></button>
        <button onClick={() => setShowGrid(true)} className="p-2 text-white/60 hover:text-white transition" title="Grid (G)"><Grid3X3 className="w-5 h-5" /></button>
        <button onClick={prev} disabled={current === 0} className="p-2 text-white/60 hover:text-white disabled:text-white/20 transition"><ChevronLeft className="w-6 h-6" /></button>
        <span className="text-white/60 text-sm font-mono min-w-[80px] text-center">{current + 1} / {TOTAL_SLIDES}</span>
        <button onClick={next} disabled={current === TOTAL_SLIDES - 1} className="p-2 text-white/60 hover:text-white disabled:text-white/20 transition"><ChevronRight className="w-6 h-6" /></button>
        <button onClick={toggleFullscreen} className="p-2 text-white/60 hover:text-white transition" title="Fullscreen (F)">
          {isFullscreen ? <Minimize className="w-5 h-5" /> : <Maximize className="w-5 h-5" />}
        </button>
      </div>

      {/* Progress */}
      <div className="absolute bottom-0 left-0 right-0 h-1 bg-white/5">
        <div className="h-full bg-[#2dd4bf] transition-all duration-300" style={{ width: `${((current + 1) / TOTAL_SLIDES) * 100}%` }} />
      </div>

      {/* Click zones */}
      <div className="absolute inset-y-0 left-0 w-1/3 cursor-w-resize z-10" onClick={prev} />
      <div className="absolute inset-y-0 right-0 w-1/3 cursor-e-resize z-10" onClick={next} />
    </div>
  );
};

export default NetunoPresentationPage;
